import type { VariantData, PendingVariantsResponse, ConfirmResponse } from '../types/confirmVar';
import { API_ENDPOINTS } from '../config/api';
import { apiClient } from './apiClient';
class VariantConfirmationService {
  /**
   * 获取待确认的基因变异数据
   */
  async getPendingVariants(fileId: string, authString?: string): Promise<PendingVariantsResponse> {
    try {
      const response = await apiClient.get<PendingVariantsResponse>(
        API_ENDPOINTS.VARIANT_CONFIRMATION.PENDING_VARIANTS(fileId),
        authString
      );  
      
      if (response.error) {
        throw new Error(response.error);
      }

      // 处理后端返回的数据结构
      if (response.data) {
        return {
          file_id: response.data.file_id || fileId,
          file_name: response.data.file_name || '',
          // ✅ 新增：处理文件级别的上下文信息（直接展开字段）
          tumor_type: response.data.tumor_type || '',
          co_variants: response.data.co_variants || '',
          variants: response.data.variants || [],
          count: response.data.count || 0,
          status: response.data.status || 'none',
          missing_fields: response.data.missing_fields || [],
        };
      }

      return {
        file_id: fileId,
        file_name: '',
        // ✅ 新增：默认的文件上下文信息
        tumor_type: '',
        co_variants: '',
        variants: [],
        count: 0,
        status: 'none',
        missing_fields: [],
      };

    } catch (error) {
      console.error('获取待确认变异数据失败:', error);
      throw error;
    }
  }

  /**
   * 对话式补充：把用户文字说明合并进待确认数据
   */
  async refineVariants(fileId: string, note: string, authString?: string): Promise<PendingVariantsResponse> {
    const response = await apiClient.post<PendingVariantsResponse>(
      API_ENDPOINTS.VARIANT_CONFIRMATION.REFINE_VARIANTS(fileId),
      { note },
      authString
    );
    if (response.error) throw new Error(response.error);
    return response.data!;
  }

  /**
   * 确认基因变异数据并运行NTRK分析
   */
  async confirmVariants(fileId: string, confirmedVariants: VariantData[], tumor_type?: string, co_variants?: string, authString?: string, force = false): Promise<ConfirmResponse> {
    try {
      const payload: any = { variants: confirmedVariants, force };

      // 如果提供了文件上下文信息，一并发送
      if (tumor_type !== undefined || co_variants !== undefined) {
        payload.tumor_type = tumor_type || '';
        payload.co_variants = co_variants || '';
      }
      
      const response = await apiClient.post<ConfirmResponse>(
        API_ENDPOINTS.VARIANT_CONFIRMATION.CONFIRM_VARIANTS(fileId),
        payload,
        authString
      );

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || { message: '确认成功', variants_count: confirmedVariants.length, analysis_result: null };
    } catch (error) {
      console.error('确认变异数据失败:', error);
      throw error;
    }
  }
}

// 导出单例
export const variantConfirmationService = new VariantConfirmationService();

// 导出类型定义
export type { VariantData, PendingVariantsResponse, ConfirmResponse };